import React, { ChangeEvent, ReactElement, useEffect } from "react";
import styled from "styled-components";

import { VOUCHER } from "@config/testIds/voucher";
import { USER_NOTIFICATIONS_TEST_IDS } from "@config/testIds/userNotifications";

import { PROMO_CODE_NOTIFICATION } from "@typings/globals";

import { Column, Row } from "@ui/Atoms/Grid";
import { Input, FormError, FormSuccess } from "@ui/Atoms/Form";
import { Button, Margins, TestWrapper } from "@ui/Atoms";

import { showToast } from "@containers/Toastify";

interface Props {
  notificationType: PROMO_CODE_NOTIFICATION;
  notificationMsg: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleVoucherSubmit: () => void;
  value: string;
  voucherLabelText: string;
  applyPromoCodeText: string;
  hasActiveVoucher?: boolean;
  isDisabled?: boolean;
  isSuccessMessageHidden?: boolean;
}

export const Voucher = (props: Props): ReactElement => {
  const hasSuccess = props.notificationType === PROMO_CODE_NOTIFICATION.SUCCESS;
  const hasError = props.notificationType === PROMO_CODE_NOTIFICATION.FAILURE;

  const isDisabled = props.isDisabled || hasSuccess || props.hasActiveVoucher;

  useEffect(() => {
    if (hasSuccess && !props.isSuccessMessageHidden) {
      showToast(props.notificationMsg, {
        type: "success",
        toastId: USER_NOTIFICATIONS_TEST_IDS.VOUCHER_APPLIED,
      });
    }
  }, [hasSuccess, props.isSuccessMessageHidden]);

  return (
    <React.Fragment>
      <React.Fragment>
        <StyledRow>
          <InputColumn xs={12} sm={6}>
            <TestWrapper testId={VOUCHER.INPUT}>
              <Input
                disabled={isDisabled}
                label={props.voucherLabelText}
                name="promo-code-value"
                value={props.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.onChange(e);
                }}
                onKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    props.handleVoucherSubmit();
                  }
                }}
                hotjarAllow
              />
            </TestWrapper>
          </InputColumn>
          <Column xs={12} sm={3}>
            <Margins
              xs={["base_x2", "base_x2", null, null]}
              sm={["0", "0", null, null]}
            >
              <div>
                <TestWrapper testId={VOUCHER.APPLY}>
                  <Button
                    disabled={isDisabled}
                    size="large"
                    variant="blue"
                    onClick={props.handleVoucherSubmit}
                    type="button"
                  >
                    {props.applyPromoCodeText}
                  </Button>
                </TestWrapper>
              </div>
            </Margins>
          </Column>
        </StyledRow>
        {isDisabled && (
          <TestWrapper testId={VOUCHER.SUCCESS_MESSAGE}>
            <FormSuccess>{props.notificationMsg}</FormSuccess>
          </TestWrapper>
        )}
        {hasError && (
          <FormError testId={VOUCHER.ERROR_MESSAGE}>
            {props.notificationMsg}
          </FormError>
        )}
      </React.Fragment>
    </React.Fragment>
  );
};

const StyledRow = styled(Row)`
  align-items: flex-end;
`;

// hide input label (z-index issue)
const InputColumn = styled(Column)`
  z-index: 0;
`;
